import { gql, useMutation, useQuery } from '@apollo/client';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
    Collapse,
    Grid,
    Paper,
    Typography,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { FaCheck, FaRegEye } from 'react-icons/fa';
import { IoMdPersonAdd } from "react-icons/io";
import { MdPerson } from 'react-icons/md';
import UpgradePlan from '../../Containers/UpgradePlan';
import { MEMBERS_LIST } from '../Org/MembersTable';
import { ORG_LIST } from './Organization';

// Helper functions
const isValidEmail = (email) => {
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email?.toLowerCase());
};

const getParsedLocalStorageItem = (key, defaultValue = null) => {
    try {
        const value = localStorage.getItem(key);
        return value ? JSON.parse(value) : defaultValue;
    } catch {
        return defaultValue;
    }
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// GraphQL Queries
export const UPDATE_ROLE_INVITE_USER = gql`
    mutation UpdateInviteUserMutaion($roleId: Int!, $email: String!) {
        addInviteUserRole(roleId: $roleId, email: $email)
    }
`;

export const INVITE_USER = gql`
    mutation InviteUserMutaion($organization: [String], $userEmail: String!) {
        inviteUser(input: { organization: $organization, userEmail: $userEmail }) {
            message
            userId
        }
    }
`;

export default function FormDialog(props) {
    const role = localStorage.getItem("role") || "Viewer";
    const access = getParsedLocalStorageItem("access", { inviteUserLimit: 1 });

    const [open, setOpen] = React.useState(false);
    const [email, setemail] = React.useState("");
    const [inputValue, setInputValue] = React.useState([]);
    const [inviteRoleAs, setinviteRoleAs] = React.useState("User");
    const [emailValidation, setEmailValidation] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const userId = localStorage.getItem("userId");

    const { data: orgdata, refetch } = useQuery(ORG_LIST, {
        skip: !userId,
        variables: { userId },
    });

    const [addInviteUserRole] = useMutation(UPDATE_ROLE_INVITE_USER, {
        refetchQueries: [MEMBERS_LIST],
        onCompleted: () => setinviteRoleAs("User"),
    });

    const [inviteUser, { error: regerror }] = useMutation(INVITE_USER, {
        refetchQueries: [MEMBERS_LIST],
        onError: (err) => {
            if (err?.message === "User Already Invited") {
                enqueueSnackbar("User Already Invited!", {
                    variant: 'error',
                    anchorOrigin: { vertical: 'top', horizontal: 'right' },
                });
            }
        },
        onCompleted: () => {
            if (inviteRoleAs === "Viewer" && isValidEmail(email)) {
                addInviteUserRole({ variables: { roleId: 3, email } });
            }

            enqueueSnackbar("Invited Successfully!", {
                variant: 'success',
                anchorOrigin: { vertical: 'top', horizontal: 'right' },
            });
            setOpen(false);
        }
    });

    const handleClickOpen = () => {
        setemail("");
        setInputValue([]);
        setinviteRoleAs("User");
        setEmailValidation(false);
        setOpen(true);
    };

    const handleChangeEmail = (e) => {
        const value = e.target.value;
        setemail(value);
        setEmailValidation(!isValidEmail(value));
    };

    const handleClose = () => {
        setemail("");
        setOpen(false);
    };

    const handleInivteUser = () => {
        if (!isValidEmail(email)) {
            setEmailValidation(true);
            return;
        }

        const orgIds = inputValue.map(item => item?.id).filter(Boolean);
        if (orgIds.length === 0) {
            enqueueSnackbar("Please select at least one valid organization.", {
                variant: 'error'
            });
            return;
        }

        inviteUser({
            variables: {
                organization: orgIds,
                userEmail: email,
            }
        });
    };

    React.useEffect(() => {
        refetch?.();
    }, [refetch]);

    const orgOptions = Array.isArray(orgdata?.organizations?.nodes)
        ? orgdata.organizations.nodes.filter(org => org?.parentId === "")
        : [];

    return (
        <div>
            <Button
                disabled={role === "Viewer"}
                startIcon={<IoMdPersonAdd />}
                variant='contained'
                style={{
                    borderRadius: "24px",
                    marginLeft: "0px",
                    boxShadow: "rgba(58, 59, 69, 0.15) 0px 0.15rem 1.75rem 0px"
                }}
                disableElevation
                color="primary"
                onClick={handleClickOpen}
            >
                Invite member
            </Button>

            <Dialog
                PaperProps={{
                    elevation: 0,
                    sx: {
                        borderRadius: "12px",
                        border: "solid 1px gray",
                        boxShadow: (!email || inputValue.length === 0 || emailValidation)
                            ? "#160044 5px 5px, #160044 10px 10px"
                            : undefined
                    }
                }}
                maxWidth="xs"
                fullWidth
                open={open}
                onClose={handleClose}
            >
                <DialogTitle sx={{ pb: 0 }}>Invite User</DialogTitle>

                {access?.inviteUserLimit === 0 ? (
                    <UpgradePlan />
                ) : (
                    <div>
                        {regerror?.message === "You've reached your maximum limit of current plan. Please upgrade your plan to invite users" ? (
                            <DialogContent>
                                <DialogContentText>
                                    You've reached your maximum limit of your current plan. Please upgrade to invite more users.
                                </DialogContentText>
                                <UpgradePlan invite />
                            </DialogContent>
                        ) : (
                            <DialogContent sx={{ pt: 1 }}>
                                <Grid container spacing={1} mb={1}>
                                    <Grid item xs={6}>
                                        <Paper
                                            onClick={() => setinviteRoleAs("User")}
                                            variant='outlined'
                                            sx={{ p: 2, cursor: "pointer", bgcolor: "#f5f2f8", color: "#9c47cc" }}
                                        >
                                            <Grid container justifyContent="space-between" alignItems="center">
                                                <MdPerson style={{ fontSize: "30px" }} />
                                                {inviteRoleAs === "User" && <FaCheck style={{ fontSize: "20px" }} />}
                                            </Grid>
                                            <Typography sx={{ fontWeight: "bold" }}>Member role</Typography>
                                            <Typography variant="body2" sx={{ fontSize: "10px" }}>
                                                Will allow invited member to view, create and update
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Paper
                                            onClick={() => setinviteRoleAs("Viewer")}
                                            variant='outlined'
                                            sx={{ p: 2, cursor: "pointer", bgcolor: "#fffbc0", color: "rgb(203, 162, 0)" }}
                                        >
                                            <Grid container justifyContent="space-between" alignItems="center">
                                                <FaRegEye style={{ fontSize: "30px" }} />
                                                {inviteRoleAs === "Viewer" && <FaCheck style={{ fontSize: "20px" }} />}
                                            </Grid>
                                            <Typography sx={{ fontWeight: "bold" }}>Viewer role</Typography>
                                            <Typography variant="body2" sx={{ fontSize: "10px" }}>
                                                Will give invited member only view access.
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                <TextField
                                    autoFocus
                                    value={email}
                                    size="small"
                                    margin="dense"
                                    label="Email Address"
                                    type="email"
                                    helperText={emailValidation && "Invalid email"}
                                    error={emailValidation}
                                    fullWidth
                                    onChange={handleChangeEmail}
                                    variant="outlined"
                                />

                                <Autocomplete
                                    multiple
                                    id="checkboxes-tags-demo"
                                    options={orgOptions}
                                    disableCloseOnSelect
                                    size="small"
                                    value={inputValue}
                                    onChange={(e, value) => setInputValue(value)}
                                    getOptionLabel={(option) => option.name || ""}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props}>
                                            <Checkbox
                                                icon={icon}
                                                checkedIcon={checkedIcon}
                                                style={{ marginRight: 8 }}
                                                checked={selected}
                                            />
                                            {option.name}
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Select Organization(s)" placeholder="Search..." />
                                    )}
                                />
                            </DialogContent>
                        )}

                        {regerror?.message === "You've reached your maximum limit of current plan. Please upgrade your plan to invite users" ? (
                            <DialogActions>
                                <Button onClick={handleClose}>Close</Button>
                            </DialogActions>
                        ) : (
                            <Collapse in={!emailValidation && !!email && inputValue.length > 0}>
                                <DialogActions>
                                    <Button fullWidth onClick={handleClose}>Close</Button>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        disableElevation
                                        fullWidth
                                        disabled={!email || emailValidation || inputValue.length === 0}
                                        onClick={handleInivteUser}
                                    >
                                        Submit
                                    </Button>
                                </DialogActions>
                            </Collapse>
                        )}
                    </div>
                )}
            </Dialog>
        </div>
    );
}
